import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { getAdminNotifications } from '../../graphql/queries';
import { Table, Card, Input, Tag, Space, Tooltip, Button, Switch } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import '../Audios/Audios.css';

const { Search } = Input;

const Notifications = () => {
  const navigate = useNavigate();
  const [rcUserId, setRcUserId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const { loading, data, refetch } = useQuery(getAdminNotifications, {
    variables: {
      rcUserId: rcUserId || null,
      page,
      pageSize,
      unreadOnly,
    },
  });

  const handleSearch = (value) => {
    setRcUserId(value);
    setSearchValue(value);
    setPage(1);
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      LIKE: 'blue',
      MILESTONE_10: 'green',
      MILESTONE_25: 'green',
      MILESTONE_50: 'gold',
      MILESTONE_100: 'red',
      FEED_CONFIRMED: 'cyan',
      VIDEO_COMPLETED: 'green',
      VIDEO_FAILED: 'red',
      CREDIT_RECEIVED: 'purple',
      DAILY_STREAK: 'orange',
      PROMPT_COPY: 'magenta',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color={getNotificationTypeColor(type)}>
          {type}
        </Tag>
      ),
      filters: [
        { text: 'LIKE', value: 'LIKE' },
        { text: 'MILESTONE_10', value: 'MILESTONE_10' },
        { text: 'MILESTONE_25', value: 'MILESTONE_25' },
        { text: 'MILESTONE_50', value: 'MILESTONE_50' },
        { text: 'MILESTONE_100', value: 'MILESTONE_100' },
        { text: 'FEED_CONFIRMED', value: 'FEED_CONFIRMED' },
        { text: 'VIDEO_COMPLETED', value: 'VIDEO_COMPLETED' },
        { text: 'VIDEO_FAILED', value: 'VIDEO_FAILED' },
        { text: 'CREDIT_RECEIVED', value: 'CREDIT_RECEIVED' },
        { text: 'DAILY_STREAK', value: 'DAILY_STREAK' },
        { text: 'PROMPT_COPY', value: 'PROMPT_COPY' },
        { text: 'OTHER', value: 'OTHER' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Tooltip title="Click to view user details">
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${record.userId}`);
            }}
            style={{ padding: 0 }}
          >
            {record.user?.rcUserId || record.userId}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Body',
      dataIndex: 'body',
      key: 'body',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: (isRead) => (
        <Tag color={isRead ? 'default' : 'blue'}>
          {isRead ? 'Read' : 'Unread'}
        </Tag>
      ),
      filters: [
        { text: 'Read', value: true },
        { text: 'Unread', value: false },
      ],
      onFilter: (value, record) => record.isRead === value,
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 160,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
    },
  ];

  const notifications = data?.getAdminNotifications?.notifications || [];
  const totalCount = data?.getAdminNotifications?.totalCount || 0;

  return (
    <div className="audios-container">
      <div className="audios-header">
        <p className="audios-breadcrumb">Pages / Notifications</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="audios-title">Notifications</h1>
          <Space>
            <span style={{ color: '#67748e', fontSize: '0.875rem' }}>Unread Only:</span>
            <Switch
              checked={unreadOnly}
              onChange={(checked) => {
                setUnreadOnly(checked);
                setPage(1);
              }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      <Card className="audios-card">
        <div className="filters-container">
          <Search
            placeholder="Search by RC User ID"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} notifications`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
          }}
          scroll={{ x: 1000 }}
          rowClassName={(record) => !record.isRead ? 'unread-row' : ''}
        />
      </Card>
    </div>
  );
};

export default Notifications;


